import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../../Graphql/utils";
import {
  createExpenseRequestQuery,
  educationDashboardSummaryQuery,
  listExpenseRequestsQuery,
  submitExpenseRequestQuery
} from "../../../Graphql/querries/education/platformQuery";
import {
  GraphQLCodePayload,
  GraphQLData,
  unwrapGraphQLOperation
} from "./educationGraphQLUtils";

export type ExpenseRequest = {
  id?: string;
  title?: string;
  category?: string;
  amount?: number;
  currency?: string;
  status?: string;
};

type ListExpenseRequestsPayload = GraphQLCodePayload & {
  expenses?: ExpenseRequest[];
};

type EducationDashboardSummaryPayload = GraphQLCodePayload & {
  pendingExpenseTotal?: number;
  approvedExpenseTotal?: number;
  expenseCount?: number;
  overdueInvoiceCount?: number;
  feeCollectedTotal?: number;
  feeOutstandingTotal?: number;
  lowStockCount?: number;
};

export const useExpenses = (input?: { page?: number; size?: number }) => {
  const { setAlert } = useContext(AlertBannerContext);
  return useQuery({
    queryKey: ["expenses", input?.page ?? 0],
    queryFn: async (): Promise<ExpenseRequest[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListExpenseRequestsPayload, "listExpenseRequests">,
        { input: object }
      >(listExpenseRequestsQuery, { input: input ?? {} }, setAlert);
      const response = unwrapGraphQLOperation(data, "listExpenseRequests");
      return response.code === API_RESPONSE_CODE.OK
        ? (response.expenses ?? [])
        : [];
    }
  });
};

export const useCreateExpense = () => {
  const { setAlert } = useContext(AlertBannerContext);
  return useMutation({
    mutationFn: async (input: {
      title: string;
      category?: string;
      amount: number;
      currency?: string;
    }) => {
      const data = await fetchGraphQL<
        GraphQLData<GraphQLCodePayload, "createExpenseRequest">,
        { input: object }
      >(createExpenseRequestQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createExpenseRequest");
      if (response.code !== API_RESPONSE_CODE.OK) {
        throw new Error(response.code);
      }
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.OK,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useSubmitExpense = () => {
  const { setAlert } = useContext(AlertBannerContext);
  return useMutation({
    mutationFn: async (input: { expenseId: string }) => {
      const data = await fetchGraphQL<
        GraphQLData<GraphQLCodePayload, "submitExpenseRequest">,
        { input: object }
      >(submitExpenseRequestQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "submitExpenseRequest");
      if (response.code !== API_RESPONSE_CODE.OK) {
        throw new Error(response.code);
      }
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.OK,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useEducationDashboardSummary = () => {
  const { setAlert } = useContext(AlertBannerContext);
  return useQuery({
    queryKey: ["educationDashboardSummary"],
    queryFn: async () => {
      const data = await fetchGraphQL<
        GraphQLData<
          EducationDashboardSummaryPayload,
          "educationDashboardSummary"
        >,
        Record<string, never>
      >(educationDashboardSummaryQuery, {}, setAlert);
      const response = unwrapGraphQLOperation(
        data,
        "educationDashboardSummary"
      );
      return response.code === API_RESPONSE_CODE.OK ? response : null;
    }
  });
};
